import { Link } from "react-router-dom";
import { LegalLayout, H2, P, UL } from "@/components/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout title="Terms & Conditions" effective="June 2026">
      <H2>1. Who we are and acceptance of these terms</H2>
      <P>
        These Terms &amp; Conditions ("Terms") govern your use of the Pocket Docta app and website
        (together, the "Service"), operated by Pocket Docta Ltd, a company registered in England and
        Wales with company number 17356731, registered office at 71 Alderton Road, London, CR0 6HL
        ("Pocket Docta", "we", "us", "our").
      </P>
      <P>
        By creating an account or using the Service, you agree to these Terms and to our{" "}
        <Link to="/privacy" className="underline underline-offset-2 text-[#EF9F27]">Privacy Policy</Link>.
        If you do not agree, do not use the Service.
      </P>

      <H2>2. Who can use Pocket Docta</H2>
      <P>
        You must be at least 18 years old to create an account. The Service is not directed at, and
        must not be used by, anyone under 18. By using the Service, you confirm you are 18 or older
        and that any information you provide is accurate.
      </P>

      <H2>3. What Pocket Docta is — and what it is not</H2>
      <P>Pocket Docta has two modes:</P>
      <UL items={[
        "Lifestyle generates personalised travel and health information — Travel Confidence Reports, accessibility guidance, emergency preparation, and curated third-party health resources — based on the health profile you provide.",
        "Care connects a patient's local treating doctor with specialist physicians for a structured written second opinion on complex cases. The specialist reviews records and shares a written opinion with the patient's own local doctor. The local doctor remains solely responsible for diagnosing, treating, and prescribing for the patient at all times.",
      ]} />
      <P>
        Pocket Docta is not a healthcare provider, is not a medical device, and does not practise
        medicine. We are a platform that facilitates access to information and to independent
        healthcare professionals. Specifically:
      </P>
      <UL items={[
        "Nothing in Lifestyle constitutes medical advice, diagnosis, or treatment. Reports are informational and generated with AI assistance from the health information you provide; they are not a substitute for professional medical advice.",
        "Nothing in Care involves Pocket Docta, or any specialist using our platform, diagnosing, treating, or prescribing directly to a patient. A specialist's written opinion is advisory input to the patient's own doctor, not a direct clinical relationship with the patient, and is never AI-generated.",
        "In a medical emergency, do not rely on the Service. Contact your local emergency services immediately.",
      ]} />

      <H2>4. Your account</H2>
      <P>
        You are responsible for keeping your login details secure and for all activity under your
        account. Tell us immediately at info@pocketdocta.com if you suspect unauthorised access. You
        must provide accurate information, including in your health profile — inaccurate information
        may affect the quality or safety of the guidance and reports the Service generates.
      </P>

      <H2>5. Specialists and local doctors — additional terms for professional users</H2>
      <P>If you register as a local doctor or specialist:</P>
      <UL items={[
        "You confirm that all licence, qualification, and identity information you provide is accurate and current, and you must notify us immediately of any change, suspension, or expiry of your professional licence.",
        "We independently verify licence information before activating your profile, and we may suspend or remove your access at any time if verification cannot be confirmed or is called into question.",
        "You act as an independent healthcare professional, not as an employee, agent, or representative of Pocket Docta. Pocket Docta does not supervise, direct, or take responsibility for your clinical judgement, the content of any opinion you write, or any treatment decision made by a local doctor.",
        "Specialists must never attempt to contact a patient directly, and must submit opinions only through the structured process provided — this is a condition of using the Service, not a preference, and exists to keep the platform operating within its intended legal structure in each country. Violating this is grounds for immediate account termination.",
        "Specialists must not include a specific prescription (drug name, dose, frequency) in a written opinion — only a general treatment approach for the local doctor to translate into an actual prescription valid in the patient's own country.",
      ]} />

      <H2>6. Payments, subscriptions, and third-party payment</H2>
      <P>
        Subscription and case fees are shown in the app before you pay. Payments are processed by
        Stripe and/or PayPal, and their own terms apply to the payment transaction itself.
      </P>
      <P>
        <strong>Third-party payment:</strong> anyone may pay for a user's subscription or case via a
        payment link, without creating an account. A third-party payer sees only the recipient's
        first name, the item being paid for, and the price — never any health information. Payment by
        a third party does not give them any access to, or rights over, the recipient's account or
        data.
      </P>
      <P>
        <strong>Refunds:</strong> Subscription fees are non-refundable once a billing period has
        started; you may cancel at any time to stop future renewals. Case fees are refundable in full
        if a case has not yet been assigned to a specialist, and non-refundable once a specialist has
        begun reviewing the case.
      </P>
      <P>
        We may change prices with notice; continued use after a price change constitutes acceptance
        of the new price for future billing periods.
      </P>

      <H2>7. Acceptable use</H2>
      <P>You agree not to:</P>
      <UL items={[
        "Use the Service for any purpose other than your own (or, where permitted, a family member's) health and travel information needs",
        "Attempt to contact a specialist directly outside the structured Care process",
        "Provide false information, including false licence credentials as a professional user",
        "Attempt to access another user's account or data without authorisation",
        "Use the Service in a medical emergency in place of contacting emergency services",
        "Upload unlawful, harmful, or infringing content",
        "Attempt to reverse-engineer, scrape, or interfere with the Service's normal operation",
      ]} />
      <P>We may suspend or terminate accounts that violate these terms.</P>

      <H2>8. Content you provide</H2>
      <P>
        You retain ownership of the documents, health information, and other content you upload. By
        uploading it, you grant Pocket Docta a licence to store, process, and share it strictly as
        needed to provide the Service to you (for example, sharing a document with a local doctor
        you've explicitly selected). We do not claim ownership of your content and do not use it for
        any purpose beyond providing the Service, as described in our Privacy Policy.
      </P>

      <H2>9. Third-party content</H2>
      <P>
        Lifestyle's Resources section links to videos and materials produced by third-party
        organisations (such as the NHS or condition-specific charities). We select these sources
        carefully, but we do not produce, control, or guarantee the accuracy of third-party content,
        and linking to it does not constitute our endorsement or medical advice.
      </P>

      <H2>10. Disclaimers</H2>
      <P>
        The Service is provided "as is". To the fullest extent permitted by law, we disclaim all
        warranties, express or implied, including that the Service will be uninterrupted, error-free,
        or that any report, opinion, or piece of information will be accurate, complete, or suitable
        for your specific circumstances. AI-generated content in Lifestyle may contain errors and
        should not be relied upon as your sole source of health or safety information.
      </P>

      <H2>11. Limitation of liability</H2>
      <P>To the fullest extent permitted by law:</P>
      <UL items={[
        "Pocket Docta is not liable for any clinical decision, diagnosis, treatment, or outcome arising from a local doctor's or specialist's independent professional judgement — these are the responsibility of the treating professional, not the platform.",
        "Pocket Docta's total liability to you for any claim arising from your use of the Service is limited to the amount you paid us in the 12 months before the claim arose, except where liability cannot be limited by law (such as death or personal injury caused by our negligence, or fraud).",
        "We are not liable for indirect, incidental, or consequential losses.",
      ]} />

      <H2>12. Indemnity</H2>
      <P>
        You agree to indemnify Pocket Docta against claims, losses, or expenses arising from your
        breach of these Terms, misuse of the Service, or (for professional users) any claim arising
        from your clinical conduct or the accuracy of credentials you provided.
      </P>

      <H2>13. Changes to the Service and these Terms</H2>
      <P>
        We may update the Service and these Terms over time, particularly as Care expands into new
        countries with their own regulatory requirements. We will notify you of material changes via
        the app or by email before they take effect. Continued use after changes take effect
        constitutes acceptance.
      </P>

      <H2>14. Termination</H2>
      <P>
        You may close your account at any time via account settings, which triggers deletion of your
        data as described in our Privacy Policy. We may suspend or terminate your account for breach
        of these Terms, suspected fraud, failed licence verification (professional users), or if
        required by law.
      </P>

      <H2>15. Complaints and disputes</H2>
      <P>
        If you have a complaint about the Service, contact us at info@pocketdocta.com. We will
        acknowledge your complaint within 30 days and aim to provide a full response as soon as
        reasonably possible thereafter.
      </P>

      <H2>16. Governing law</H2>
      <P>
        These Terms are governed by the laws of England and Wales, and any dispute is subject to the
        exclusive jurisdiction of the courts of England and Wales.
      </P>

      <H2>17. General</H2>
      <P>
        If any part of these Terms is found unenforceable, the rest remains in effect. These Terms,
        together with our Privacy Policy, are the entire agreement between you and Pocket Docta
        regarding the Service.
      </P>

      <H2>18. Contact us</H2>
      <P>
        Pocket Docta Ltd · Email: info@pocketdocta.com · Registered office: 71 Alderton Road, London,
        CR0 6HL · Company number: 17356731.
      </P>
    </LegalLayout>
  );
}
